import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Row, Col, Card, Statistic, Tabs, List, Avatar, Tag, Input, Button, message, Progress, Empty, Popconfirm } from 'antd';
import { adminAPI } from '../api/admin';

const houseColor = {
  Gryffindor: '#740001', Slytherin: '#1a472a', Hufflepuff: '#f0ad4e', Ravenclaw: '#0e1a40'
};

export default function TrainManager() {
  const [overview, setOverview] = useState(null);
  const [messages, setMessages] = useState([]);
  const [banned, setBanned] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef(null);

  const loadOverview = useCallback(async () => {
    try {
      const r = await adminAPI.getTrainOverview();
      if (r.data.success) setOverview(r.data);
    } catch (_) {}
  }, []);

  const loadChat = useCallback(async () => {
    try {
      const r = await adminAPI.getTrainChat();
      if (r.data.success) setMessages(r.data.messages || []);
    } catch (_) {}
  }, []);

  const loadBanned = useCallback(async () => {
    try {
      const r = await adminAPI.getTrainBanned();
      if (r.data.success) setBanned(r.data.users || []);
    } catch (_) {}
  }, []);

  useEffect(() => {
    loadOverview();
    loadChat();
    loadBanned();
    const id = setInterval(() => { loadOverview(); loadChat(); }, 5000);
    return () => clearInterval(id);
  }, [loadOverview, loadChat, loadBanned]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    setSending(true);
    try {
      const r = await adminAPI.postTrainChat(text);
      if (r.data.success) {
        setInput('');
        loadChat();
      }
    } catch (_) {
      message.error('ارسال پیام ناموفق بود');
    } finally {
      setSending(false);
    }
  };

  const unban = async (id) => {
    try {
      await adminAPI.unbanTrainUser(id);
      message.success('دسترسی کاربر بازگردانده شد');
      loadBanned();
      loadOverview();
    } catch (_) {
      message.error('عملیات ناموفق بود');
    }
  };

  const stats = overview?.stats || {};

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} lg={6}>
          <Card><Statistic title="در ایستگاه" value={stats.atStation ?? 0} /></Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card>
            <Statistic
              title={overview?.isMoving ? '🚂 در حال حرکت' : 'در قطار'}
              value={stats.onTrain ?? 0}
              valueStyle={{ color: overview?.isMoving ? '#52c41a' : undefined }}
            />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card><Statistic title="رسیده به هاگوارتز" value={stats.arrived ?? 0} /></Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card><Statistic title="مسدود شده" value={stats.banned ?? 0} valueStyle={{ color: '#cf1322' }} /></Card>
        </Col>
      </Row>

      <Tabs
        defaultActiveKey="passengers"
        items={[
          {
            key: 'passengers',
            label: `مسافران قطار (${(overview?.passengers || []).length})`,
            children: (
              <List
                dataSource={overview?.passengers || []}
                locale={{ emptyText: <Empty description="قطاری در حال حرکت نیست" /> }}
                renderItem={(p) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar src={p.profileImage}>{p.name?.slice(0, 1)}</Avatar>}
                      title={
                        <span>
                          {p.name}{' '}
                          {p.house && <Tag color={houseColor[p.house]}>{p.house}</Tag>}
                          {p.warnings > 0 && <Tag color="orange">اخطار: {p.warnings}</Tag>}
                          {p.banned && <Tag color="red">مسدود</Tag>}
                        </span>
                      }
                      description={<Progress percent={p.progressPct} size="small" />}
                    />
                  </List.Item>
                )}
              />
            )
          },
          {
            key: 'chat',
            label: 'گفتگوی قطار',
            children: (
              <Card>
                <div style={{ maxHeight: 400, overflowY: 'auto', marginBottom: 12 }}>
                  {messages.length === 0 && <Empty description="پیامی نیست" />}
                  {messages.map((m) => (
                    <div key={m.id} style={{ marginBottom: 8, padding: 8, borderRadius: 8, background: m.flagged ? '#fff1f0' : m.isAdmin ? '#fffbe6' : '#f5f5f5' }}>
                      <div style={{ fontSize: 12, color: '#888' }}>
                        {m.isAdmin ? `🎩 ${m.name}` : m.name}
                        {m.house && <Tag style={{ marginRight: 6 }} color={houseColor[m.house]}>{m.house}</Tag>}
                        {m.flagged && <Tag color="red">متخلف: {m.flagReason}</Tag>}
                        <span style={{ marginRight: 8 }}>{new Date(m.createdAt).toLocaleString('fa-IR')}</span>
                      </div>
                      <div>{m.content}</div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
                <Input.Group compact style={{ display: 'flex' }}>
                  <Input
                    style={{ flex: 1 }}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onPressEnter={send}
                    placeholder="به‌عنوان مدیر پیام بگذارید..."
                    maxLength={1000}
                  />
                  <Button type="primary" loading={sending} onClick={send}>ارسال</Button>
                </Input.Group>
              </Card>
            )
          },
          {
            key: 'banned',
            label: `کاربران متخلف (${banned.length})`,
            children: (
              <List
                dataSource={banned}
                locale={{ emptyText: <Empty description="کاربر متخلفی نیست" /> }}
                renderItem={(u) => (
                  <List.Item
                    actions={u.banned ? [<Popconfirm title="رفع مسدودی این کاربر؟" onConfirm={() => unban(u.id)} okText="بله" cancelText="خیر"><a>رفع مسدودی</a></Popconfirm>] : []}
                  >
                    <List.Item.Meta
                      title={
                        <span>
                          {u.name} <span style={{ color: '#888', fontSize: 12 }}>{u.phoneNumber}</span>{' '}
                          {u.banned ? <Tag color="red">مسدود</Tag> : <Tag color="orange">اخطار: {u.warnings}</Tag>}
                          {u.banReason && <Tag>{u.banReason}</Tag>}
                        </span>
                      }
                      description={
                        <div>
                          {(u.flaggedMessages || []).map((fm, i) => (
                            <div key={i} style={{ color: '#cf1322', fontSize: 13 }}>
                              «{fm.content}» — <span style={{ color: '#888' }}>{fm.reason}</span>
                            </div>
                          ))}
                          {(u.flaggedMessages || []).length === 0 && <span style={{ color: '#888' }}>بدون پیام ثبت‌شده</span>}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )
          }
        ]}
      />
    </div>
  );
}
